<script setup lang="ts">
/**
 * The index's one setting, and the coverage check.
 *
 * The index itself has nothing to edit: it is built from the tags. What sits
 * here instead is the check, and this is the right place for it. A standard
 * declared material with nothing disclosed under it is a finding about the
 * **document**, not about the page, so it belongs where somebody is working
 * rather than printed into what they send out.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { gapsOf } from '@/domain/esrsIndex'
import type { DisclosureIndexSection } from '@/domain/section'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

const props = defineProps<{ section: DisclosureIndexSection }>()
const emit = defineEmits<{ change: [section: DisclosureIndexSection] }>()

const report = useReportStore()

const gaps = computed(() => gapsOf(report.sections))

function set(changes: Partial<DisclosureIndexSection>): void {
  emit('change', { ...props.section, ...changes })
}

function wording(gap: ReturnType<typeof gapsOf>[number]): string {
  return gap.kind === 'declaredNotDisclosed'
    ? t('inspector.index.gaps.declaredNotDisclosed', { standard: gap.standard })
    : t('inspector.index.gaps.duplicated', { ref: gap.ref })
}
</script>

<template>
  <div class="fields">
    <p class="lead">{{ t('inspector.index.lead') }}</p>

    <label class="row">
      <span class="row__text">
        <span class="row__label">{{ t('inspector.index.showTitles') }}</span>
        <span class="row__hint">{{ t('inspector.index.showTitlesHint') }}</span>
      </span>
      <input
        type="checkbox"
        :checked="section.showTitles"
        @change="set({ showTitles: ($event.target as HTMLInputElement).checked })"
      />
    </label>

    <section class="check">
      <p class="check__title">{{ t('inspector.index.check') }}</p>

      <p v-if="gaps.length === 0" class="check__clear">{{ t('inspector.index.noGaps') }}</p>

      <ul v-else class="check__gaps">
        <li v-for="(gap, at) in gaps" :key="at">{{ wording(gap) }}</li>
      </ul>
    </section>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.lead {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.row {
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  cursor: pointer;
}

.row__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.row__label {
  color: var(--ink);
  font-size: var(--text-base);
}

.row__hint {
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}

.row input {
  accent-color: var(--accent);
}

.check {
  padding: var(--sp-4) var(--sp-5);
  border-radius: var(--radius);
  background: var(--panel-raised);
  box-shadow: inset 0 0 0 1px var(--hairline);
}

.check__title {
  margin: 0 0 var(--sp-3);
  color: var(--ink-faint);
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.check__clear {
  margin: 0;
  color: var(--good);
  font-size: var(--text-sm);
  line-height: 1.5;
}

.check__gaps {
  margin: 0;
  padding-left: var(--sp-6);
  color: var(--warn);
  font-size: var(--text-sm);
  line-height: 1.55;
}
</style>
